import axios from "axios";

// Ensure API_URL always ends with /api/
const getApiUrl = () => {
    const baseUrl = process.env.REACT_APP_BASEURL_CLIENT_BACKEND || "https://localhost:4000";
    return baseUrl.replace(/\/+$/, '') + '/';
};

const API_URL = getApiUrl();

// Helper function to get auth headers
const getAuthHeaders = () => {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    return {
        'Content-Type': 'application/json',
        'Authorization': userDetails?.token ? `Bearer ${userDetails.token}` : ''
    };
};

class CardTemplateService {
    
    // Template Management
    async getTemplates(filters = {}) {
        const params = new URLSearchParams();
        Object.keys(filters).forEach(key => {
            if (filters[key]) {
                params.append(key, filters[key]);
            }
        });
        
        const response = await axios.get(
            API_URL + "card/templates?" + params.toString(),
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async getTemplateById(templateId) {
        const response = await axios.get(
            API_URL + `card/templates/${templateId}`,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async createTemplate(templateData) {
        const response = await axios.post(
            API_URL + "card/templates",
            templateData,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async updateTemplate(templateId, templateData) {
        const response = await axios.put(
            API_URL + `card/templates/${templateId}`,
            templateData,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async deleteTemplate(templateId) {
        const response = await axios.delete(
            API_URL + `card/templates/${templateId}`,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    // Template Preview and Testing
    async previewTemplate(previewData) {
        const response = await axios.post(
            API_URL + "card/templates/preview",
            previewData,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async getTemplatePreview(templateId, side) {
        const response = await axios.get(
            API_URL + `card/templates/${templateId}/preview/${side}`,
            { 
                headers: getAuthHeaders(),
                responseType: 'blob'
            }
        );
        return response.data;
    }

    async testTemplate(templateId, testData) {
        const response = await axios.post(
            API_URL + `card/templates/${templateId}/test`,
            testData,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    // Template Categories and Defaults
    async getDefaultTemplates(countryCode) {
        const response = await axios.get(
            API_URL + `card/templates/defaults?countryCode=${countryCode}`,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async setDefaultTemplate(defaultData) {
        const response = await axios.post(
            API_URL + "card/templates/defaults",
            defaultData,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async duplicateTemplate(templateId, duplicateData) {
        const response = await axios.post(
            API_URL + `card/templates/${templateId}/duplicate`,
            duplicateData,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    // Card Management
    async getCardById(cardId) {
        const response = await axios.get(
            API_URL + `Cards/${cardId}`,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async getCardsByBeneficiary(beneficiaryId) {
        const response = await axios.get(
            API_URL + `Cards/Beneficiary/${beneficiaryId}`,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async getCardByNumber(cardNumber) {
        const response = await axios.get(
            API_URL + `card/number/${cardNumber}`,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    // Card Production
    async initiateCardProduction(productionData) {
        const response = await axios.post(
            API_URL + "card/initiate-production",
            productionData,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async getProductionStatus(cardId) {
        const response = await axios.get(
            API_URL + `card/production-status/${cardId}`,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async updateProductionStepStatus(stepId, statusData) {
        const response = await axios.put(
            API_URL + `card/production-step/${stepId}/status`,
            statusData,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async retryProductionStep(stepId, retryReason) {
        const response = await axios.post(
            API_URL + `card/production-step/${stepId}/retry`,
            { retryReason },
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    // Card Status Management
    async updateCardStatus(cardId, statusData) {
        const response = await axios.put(
            API_URL + `card/${cardId}/status`,
            statusData,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async activateCard(cardId, activationData) {
        const response = await axios.post(
            API_URL + `card/${cardId}/activate`,
            activationData,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async blockCard(cardId, blockData) {
        const response = await axios.post(
            API_URL + `card/${cardId}/block`,
            blockData,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async unblockCard(cardId, unblockData) {
        const response = await axios.post(
            API_URL + `card/${cardId}/unblock`,
            unblockData,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    // Batch Management
    async getCardBatches(countryCode) {
        const params = countryCode ? `?countryCode=${countryCode}` : '';
        const response = await axios.get(
            API_URL + `card/batches${params}`,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async getBatchById(batchId) {
        const response = await axios.get(
            API_URL + `card/batches/${batchId}`,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async createCardBatch(batchData) {
        const response = await axios.post(
            API_URL + "card/batches",
            batchData,
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    async completeBatch(batchId) {
        const response = await axios.post(
            API_URL + `card/batches/${batchId}/complete`,
            {},
            { headers: getAuthHeaders() }
        );
        return response.data;
    }

    // Card Preview and Print
    async generateCardPreview(cardId, templateId = null) {
        const params = templateId ? `?templateId=${templateId}` : '';
        const response = await axios.get(
            API_URL + `Cards/${cardId}/preview${params}`,
            { 
                headers: getAuthHeaders(),
                responseType: 'blob'
            }
        );
        return response.data;
    }

    async printCard(cardId, printOptions = {}) {
        const response = await axios.post(
            API_URL + `card/${cardId}/print`,
            printOptions,
            { 
                headers: getAuthHeaders(),
                responseType: 'blob'
            }
        );
        return response.data;
    }

    async batchPrintCards(cardIds, printOptions = {}) {
        const response = await axios.post(
            API_URL + "card/batch-print",
            { cardIds, ...printOptions },
            { 
                headers: getAuthHeaders(),
                responseType: 'blob'
            }
        );
        return response.data;
    }

    // Helper method to create blob URL for preview
    createPreviewUrl(blob) {
        return URL.createObjectURL(blob);
    }

    // Helper method to download file
    downloadFile(blob, filename) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
}

export default new CardTemplateService();